/**
 * The tracker dog's skeleton (ROUND8 §3): fourteen bones, their parents and rest offsets. Like the soldier's
 * rig (rig.ts) it faces +z, but it is built at its DRAWN size - already the larger-than-life scale the men are
 * drawn at (05 §3) - so the mesh is never scaled: about 0.6 m at the withers and 0.9 m to the ear tips, the
 * height the aim is given for a dog (ROUND8 §4).
 */

import { Vector3 } from 'three';

export const D = {
  root: 0,
  /** The middle of the back: pitching it tips the whole dog (the sit), rolling it lays the body down. */
  body: 1,
  /** Where the neck leaves the chest. Nose down and head up are mostly this bone. */
  neck: 2,
  head: 3,
  /** The lower jaw, hinged under the back of the muzzle: open is the bark. */
  jaw: 4,
  tail: 5,
  lFore: 6,
  lForeLow: 7,
  rFore: 8,
  rForeLow: 9,
  lHind: 10,
  lHindLow: 11,
  rHind: 12,
  rHindLow: 13,
} as const;

export const DOG_BONE_COUNT = 14;

/** Height of the body bone, metres: the torso's middle, 0.16 m under the withers. */
export const DOG_BODY_Y = 0.44;

/** Rest pose: parent index and offset from the parent, metres, facing +z. */
export const DOG_REST: ReadonlyArray<readonly [parent: number, x: number, y: number, z: number]> = [
  [-1, 0, 0, 0],
  [D.root, 0, DOG_BODY_Y, 0],
  [D.body, 0, 0.03, 0.3],
  [D.neck, 0, 0.22, 0.12],
  [D.head, 0, -0.06, 0.07],
  [D.body, 0, 0.1, -0.38],
  [D.body, 0.09, -0.02, 0.26],
  [D.lFore, 0, -0.2, 0],
  [D.body, -0.09, -0.02, 0.26],
  [D.rFore, 0, -0.2, 0],
  [D.body, 0.09, 0, -0.27],
  [D.lHind, 0, -0.21, 0],
  [D.body, -0.09, 0, -0.27],
  [D.rHind, 0, -0.21, 0],
];

/** Front and hind legs as [upper, lower] pairs, left first: the gait and the sit walk them in this order. */
export const FORE_LEGS = [[D.lFore, D.lForeLow], [D.rFore, D.rForeLow]] as const;
export const HIND_LEGS = [[D.lHind, D.lHindLow], [D.rHind, D.rHindLow]] as const;

/** Lower legs, joint to sole: with the upper (0.2, 0.21) a foreleg is 0.42 m long and a hind leg 0.44. */
export const FORE_LOWER = 0.22;
export const HIND_LOWER = 0.23;

/** The paw's heel and toe along the lower leg's z, metres: `plant` stands on whichever reaches lower. */
export const PAW_HEEL = -0.05;
export const PAW_TOE = 0.09;

/**
 * The tip of the nose, in the head bone's frame: the model puts the black of the nose here, and the tests
 * measure the casting tell (nose to the ground) and the baying one (nose in the air) from it.
 */
export const DOG_NOSE: readonly [x: number, y: number, z: number] = [0, -0.005, 0.25];

/** Where a bone sits in the rest pose, in the dog's own frame. */
export function dogRestWorld(index: number): Vector3 {
  const out = new Vector3();
  let i = index;
  while (i >= 0) {
    const r = DOG_REST[i];
    if (!r) break;
    out.x += r[1];
    out.y += r[2];
    out.z += r[3];
    i = r[0];
  }
  return out;
}
